/**
 * Service for managing MCP Actions (Tools)
 * Uses the RagApiClient to communicate with the backend
 */

import { ragApi } from './ApiService'
import type {
  MCPAction,
  MCPActionCreate,
  MCPProvider,
  MCPActionStats,
  ExternalMCPHandshakeResponse,
  ExternalMCPImportResponse,
  ExternalMCPToolsPreviewResponse,
  ExternalMCPExploreResponse,
  WorkspaceSecret,
} from '@/types/tool'

function errorMentionsClassification(error: unknown): boolean {
  const candidate = error as {
    body?: unknown
    response?: { data?: unknown }
    data?: unknown
    message?: unknown
  } | undefined

  const maybeResponse = candidate?.body ?? candidate?.response?.data ?? candidate?.data

  const chunks: string[] = []
  if (typeof maybeResponse === 'string') chunks.push(maybeResponse)
  if (maybeResponse && typeof maybeResponse === 'object') {
    chunks.push(JSON.stringify(maybeResponse))
  }
  if (typeof candidate?.message === 'string') {
    chunks.push(candidate.message)
  }

  const fullText = chunks.join(' ').toLowerCase()
  const mentionsClassification = fullText.includes('classification')
  const hints =
    fullText.includes('unknown field') ||
    fullText.includes('unexpected field') ||
    fullText.includes('not allowed') ||
    fullText.includes('not recognized') ||
    fullText.includes('unrecognized') ||
    fullText.includes('no permitido') ||
    fullText.includes('campo no') ||
    fullText.includes('unknown key')
  return mentionsClassification && hints
}

function withoutClassification<T extends Partial<MCPActionCreate>>(data: T): Omit<T, 'classification'> {
  const { classification, ...rest } = data
  void classification
  return rest
}

export class ToolsService {
  private apiBase = '/api/agent/v1/tools'

  private unwrapData<T>(response: unknown): T {
    if (
      response &&
      typeof response === 'object' &&
      'success' in response &&
      'data' in response
    ) {
      return (response as { data: T }).data
    }
    return response as T
  }

  /**
   * List all MCP actions (tools)
   * @param params Optional filters: provider, enabled
   */
  async listActions(params?: {
    provider?: string
    enabled?: boolean
    /** Las que crea la plataforma para el Brain.
     *
     * Se excluyen por defecto desde el 2026-09-04, y con razon: mezcladas con
     * las del cliente llenaban el Studio de herramientas ajenas que ademas no
     * se pueden editar. Desde el 2026-09-08 tienen pestana propia, asi que
     * quien las agrupa aparte si las pide.
     */
    includeGenerated?: boolean
  }): Promise<MCPAction[]> {
    const queryParams: Record<string, string> = {}
    if (params?.provider) {
      queryParams.provider = params.provider
    }
    if (params?.enabled !== undefined) {
      queryParams.enabled = params.enabled.toString()
    }
    if (params?.includeGenerated) {
      queryParams.include_generated = 'true'
    }
    
    const response = this.unwrapData<unknown>(
      await ragApi.get(`${this.apiBase}/`, { params: queryParams }) as unknown
    )
    // Handle paginated response (Django REST Framework)
    if (response && typeof response === 'object' && 'items' in response) {
      return (response as { items: MCPAction[] }).items
    }
    if (response && typeof response === 'object' && 'results' in response) {
      return (response as { results: MCPAction[] }).results
    }
    // Handle direct array response
    return Array.isArray(response) ? response : []
  }

  /**
   * Get a single action by ID
   */
  async getAction(actionId: string): Promise<MCPAction> {
    return this.unwrapData<MCPAction>(await ragApi.get(`${this.apiBase}/${actionId}/`))
  }

  /**
   * Create a new action
   */
  async createAction(data: MCPActionCreate): Promise<MCPAction> {
    try {
      return this.unwrapData<MCPAction>(await ragApi.post(`${this.apiBase}/`, data))
    } catch (error) {
      if (data.classification !== undefined && errorMentionsClassification(error)) {
        return this.unwrapData<MCPAction>(
          await ragApi.post(`${this.apiBase}/`, withoutClassification(data))
        )
      }
      throw error
    }
  }

  /**
   * Update an existing action
   */
  async updateAction(actionId: string, data: Partial<MCPActionCreate>): Promise<MCPAction> {
    try {
      return this.unwrapData<MCPAction>(await ragApi.patch(`${this.apiBase}/${actionId}/`, data))
    } catch (error) {
      if (data.classification !== undefined && errorMentionsClassification(error)) {
        return this.unwrapData<MCPAction>(
          await ragApi.patch(`${this.apiBase}/${actionId}/`, withoutClassification(data))
        )
      }
      throw error
    }
  }

  /**
   * Delete an action
   */
  async deleteAction(actionId: string): Promise<void> {
    await ragApi.delete(`${this.apiBase}/${actionId}/`)
  }

  /**
   * Enable an action
   */
  async enableAction(actionId: string): Promise<{ success: boolean; message: string }> {
    const data = this.unwrapData<MCPAction>(await ragApi.post(`${this.apiBase}/${actionId}/enable/`))
    return { success: true, message: `Tool ${data.id} enabled` }
  }

  /**
   * Disable an action
   */
  async disableAction(actionId: string): Promise<{ success: boolean; message: string }> {
    const data = this.unwrapData<MCPAction>(await ragApi.post(`${this.apiBase}/${actionId}/disable/`))
    return { success: true, message: `Tool ${data.id} disabled` }
  }

  /**
   * Get statistics for an action
   */
  async getActionStats(actionId: string): Promise<MCPActionStats> {
    return this.unwrapData<MCPActionStats>(await ragApi.get(`${this.apiBase}/${actionId}/stats/`))
  }

  /**
   * List all providers (for dropdowns)
   */
  async listProviders(): Promise<MCPProvider[]> {
    const response = await ragApi.get('/api/mcp/providers/') as unknown
    // Handle paginated response (Django REST Framework)
    if (response && typeof response === 'object' && 'results' in response) {
      return (response as { results: MCPProvider[] }).results
    }
    // Handle direct array response
    return Array.isArray(response) ? response : []
  }

  /**
   * Create a default "Webhooks" provider if none exists
   */
  async ensureDefaultWebhookProvider(): Promise<MCPProvider> {
    const providers = await this.listProviders()
    
    // Look for existing "Webhooks" provider
    const webhookProvider = providers.find(
      (p) => p.name === 'webhooks' || p.display_name.toLowerCase().includes('webhook')
    )
    
    if (webhookProvider) {
      return webhookProvider
    }

    // Create default provider
    const defaultProvider = await ragApi.post('/api/mcp/providers/', {
      name: 'webhooks',
      display_name: 'Webhooks',
      description: 'Provider para herramientas webhook personalizadas',
      category: 'api',
      is_enabled: true,
      priority: 100,
      version: '1.0.0',
      provider_class: 'mcp_system.providers.webhook.WebhookProvider',
      config_params: {},
    }) as MCPProvider

    return defaultProvider
  }

  async updateProvider(providerId: string, data: Partial<MCPProvider>): Promise<MCPProvider> {
    return this.unwrapData<MCPProvider>(await ragApi.patch(`/api/mcp/providers/${providerId}/`, data))
  }

  async externalHandshake(
    providerId: string,
    payload?: { smoke_remote_tool_name?: string; smoke_arguments?: Record<string, unknown> }
  ): Promise<ExternalMCPHandshakeResponse> {
    return this.unwrapData<ExternalMCPHandshakeResponse>(
      await ragApi.post(`/api/mcp/providers/${providerId}/external/handshake/`, payload || {})
    )
  }

  async externalToolsPreview(providerId: string): Promise<ExternalMCPToolsPreviewResponse> {
    return this.unwrapData<ExternalMCPToolsPreviewResponse>(
      await ragApi.post(`/api/mcp/providers/${providerId}/external/tools-preview/`, {})
    )
  }

  async externalImportTools(
    providerId: string,
    payload?: {
      force?: boolean
      sync_mode?: 'keep_missing' | 'deactivate_missing'
      selected_tools?: string[]
    }
  ): Promise<ExternalMCPImportResponse> {
    return this.unwrapData<ExternalMCPImportResponse>(
      await ragApi.post(`/api/mcp/providers/${providerId}/external/import-tools/`, payload || {})
    )
  }

  async externalExplore(payload: {
    server_url: string
    transport?: 'http_jsonrpc' | 'streamable_http'
    auth_type?: 'none' | 'bearer' | 'header'
    auth_header_name?: string
    auth_secret_id?: string
    timeout_seconds?: number
  }): Promise<ExternalMCPExploreResponse> {
    return this.unwrapData<ExternalMCPExploreResponse>(
      await ragApi.post('/api/mcp/system/external-explore/', payload)
    )
  }

  async listSecrets(): Promise<WorkspaceSecret[]> {
    const response = await ragApi.get('/api/mcp/secrets/') as unknown
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as { data?: unknown }).data
      if (Array.isArray(data)) return data as WorkspaceSecret[]
      if (data && typeof data === 'object' && 'results' in data) {
        return ((data as { results?: unknown }).results as WorkspaceSecret[]) || []
      }
    }
    if (response && typeof response === 'object' && 'results' in response) {
      return (response as { results: WorkspaceSecret[] }).results
    }
    return Array.isArray(response) ? response : []
  }

  async createSecret(payload: {
    name: string
    description?: string
    secret_value: string
    is_enabled?: boolean
  }): Promise<WorkspaceSecret> {
    return this.unwrapData<WorkspaceSecret>(await ragApi.post('/api/mcp/secrets/', payload))
  }

  async updateSecret(
    secretId: string,
    payload: {
      name?: string
      description?: string
      secret_value?: string
      is_enabled?: boolean
    }
  ): Promise<WorkspaceSecret> {
    return this.unwrapData<WorkspaceSecret>(await ragApi.patch(`/api/mcp/secrets/${secretId}/`, payload))
  }

  async deleteSecret(secretId: string): Promise<void> {
    await ragApi.delete(`/api/mcp/secrets/${secretId}/`)
  }
}

export const toolsService = new ToolsService()
